
import React, { useState, useEffect, useMemo } from 'react';
import { History, Search, Link as LinkIcon, Printer, CheckCircle, AlertCircle, Edit, Trash2, Unlink } from 'lucide-react';
import {
    PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip,
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, ComposedChart, Area
} from 'recharts';
import { printInternalVoucher } from '../utils/printInternalVoucher';
import internalOrderService from '../services/internalOrderService';
import { walletService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// Color palette for charts
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'];

// Helper to generate consistent colors for products
const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
    return '#' + '00000'.substring(0, 6 - c.length) + c;
};

const InternalOrdersHistoryPage = () => {
    const { isAdmin } = useAuth();

    // --- HISTORY STATE ---
    const [historyOrders, setHistoryOrders] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historySearch, setHistorySearch] = useState('');

    // Filter State
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        customer: '',
        status: 'all',
        minAmount: '',
        maxAmount: ''
    });
    const [showFilters, setShowFilters] = useState(true);

    // Analytics State
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);

    // Prepare data for Product Stacked Chart
    const productStackedData = useMemo(() => {
        if (!analytics?.productTimeline) return { data: [], products: [] };

        const timeline = {};
        const allProducts = new Set();

        // Map orders from timelineStats for easy lookup
        const ordersMap = {};
        if (analytics.timelineStats) {
            analytics.timelineStats.forEach(stat => {
                ordersMap[stat.time_label] = stat.order_count;
            });
        }

        analytics.productTimeline.forEach(item => {
            if (!timeline[item.time_label]) {
                timeline[item.time_label] = {
                    time_label: item.time_label,
                    order_count: ordersMap[item.time_label] || 0,
                    total_revenue: 0 // Calculate sum of available product details
                };
            }
            timeline[item.time_label][item.product_name] = item.revenue;
            timeline[item.time_label].total_revenue += item.revenue;
            allProducts.add(item.product_name);
        });

        return {
            data: Object.values(timeline).sort((a, b) => a.time_label.localeCompare(b.time_label)),
            products: Array.from(allProducts)
        };
    }, [analytics]);

    // Link Modal State
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [orderToLink, setOrderToLink] = useState(null);
    const [linkInvoiceSearch, setLinkInvoiceSearch] = useState('');
    const [linkInvoices, setLinkInvoices] = useState([]);

    useEffect(() => {
        loadHistory();
        if (isAdmin()) loadAnalytics();
    }, []);

    const loadHistory = async (overrideFilters = null) => {
        setHistoryLoading(true);
        try {
            const currentFilters = overrideFilters || filters;
            const params = {
                limit: 500,
                search: historySearch,
                ...currentFilters
            };
            const res = await internalOrderService.getAll(params);
            setHistoryOrders(res.data || []);
        } catch (error) {
            console.error(error);
            toast.error('Error cargando historial');
        } finally {
            setHistoryLoading(false);
        }
    };

    const loadAnalytics = async (overrideFilters = null) => {
        setAnalyticsLoading(true);
        try {
            const currentFilters = overrideFilters || filters;
            const params = {
                ...currentFilters
            };
            const res = await internalOrderService.getAnalytics(params);
            setAnalytics(res.data || null);
        } catch (error) {
            console.error(error);
            toast.error('Error cargando analytics');
        } finally {
            setAnalyticsLoading(false);
        }
    };

    const handleApplyFilters = (overrideFilters = null) => {
        // Support event object or direct filters
        const actualOverrides = (overrideFilters && overrideFilters.preventDefault) ? null : overrideFilters;
        loadHistory(actualOverrides);
        if (isAdmin()) loadAnalytics(actualOverrides);
    };

    const handleClearFilters = () => {
        const resetFilters = {
            startDate: '',
            endDate: '',
            customer: '',
            status: 'all',
            minAmount: '',
            maxAmount: ''
        };
        setFilters(resetFilters);
        setHistorySearch('');
        // Directly load with reset filters to avoid stale state
        loadHistory(resetFilters);
        if (isAdmin()) loadAnalytics(resetFilters);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            maximumFractionDigits: 0
        }).format(value || 0);
    };

    const handleHistorySearchSubmit = (e) => {
        e.preventDefault();
        loadHistory();
    };

    // --- HISTORY ACTIONS ---

    const handlePrintHistoryOrder = async (order) => {
        try {
            // Note: Standard getAll query might not include items. 
            // printInternalVoucher requires items.
            // If items missing, warn.
            let itemsToPrint = order.items || [];
            if (itemsToPrint.length === 0) {
                // Try to fetch full order details
                try {
                    const fullOrderRes = await internalOrderService.getById(order.id);
                    if (fullOrderRes.success && fullOrderRes.data.items) {
                        itemsToPrint = fullOrderRes.data.items;
                    } else {
                        toast('No se encontraron items para este pedido', { icon: '⚠️' });
                    }
                } catch (err) {
                    console.error('Error improving print details:', err);
                    toast('Imprimiendo sin detalle de items', { icon: '⚠️' });
                }
            }
            printInternalVoucher(order, itemsToPrint);
        } catch (e) {
            console.error(e);
            toast.error('Error al imprimir');
        }
    };

    const openLinkModal = (order) => {
        setOrderToLink(order);
        setLinkModalOpen(true);
        setLinkInvoiceSearch('');
        setLinkInvoices([]);
    };

    const handleSearchLinkInvoice = async (val) => {
        setLinkInvoiceSearch(val);
        if (val.length < 3) {
            setLinkInvoices([]);
            return;
        }
        try {
            const response = await walletService.getWalletOrders({ search: val });
            setLinkInvoices(response.data.orders || []);
        } catch (error) {
            console.error(error);
        }
    };

    const submitLink = async (invoice) => {
        if (!orderToLink || !invoice) return;
        try {
            await internalOrderService.linkOrder(orderToLink.id, invoice.order_number);
            toast.success('Vinculado exitosamente');
            setLinkModalOpen(false);
            setOrderToLink(null);
            loadHistory();
        } catch (error) {
            toast.error('Error al vincular');
        }
    };

    const handleUnlink = async () => {
        if (!orderToLink) return;
        if (!window.confirm('¿Estás seguro de desvincular este pedido? Quedará suelto.')) return;

        try {
            await internalOrderService.unlinkOrder(orderToLink.id);
            toast.success('Desvinculado exitosamente');
            setLinkModalOpen(false);
            setOrderToLink(null);
            loadHistory();
        } catch (error) {
            toast.error('Error al desvincular');
        }
    };

    return (
        <div className="p-6 min-h-screen">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <History className="text-blue-600" />
                    Historial de Ventas Internas
                </h1>

                <div className="flex gap-2">
                    <button onClick={loadHistory} className="bg-white border hover:bg-gray-50 text-gray-600 px-4 py-2 rounded-lg flex items-center gap-2 shadow-sm">
                        <History size={16} /> Refrescar
                    </button>
                </div>
            </div>

            {/* Collapsible Filters */}
            <div className="bg-white rounded-xl shadow-sm border mb-4 p-1">
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg group"
                >
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-blue-50 text-blue-600 rounded-md">
                            <Search size={18} />
                        </div>
                        <span className="font-semibold text-gray-700">Panel de Filtros Avanzados</span>
                    </div>
                    <div className="flex items-center gap-4">
                        {!showFilters && (
                            <div className="hidden md:flex gap-2">
                                {filters.startDate && <span className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-500">Desde: {filters.startDate}</span>}
                                {filters.customer && <span className="text-xs bg-blue-50 px-2 py-1 rounded text-blue-600">Cliente: {filters.customer}</span>}
                            </div>
                        )}
                        <span className="text-gray-400 group-hover:text-blue-600 transition-colors">{showFilters ? '▲ Ocultar' : '▼ Ver Filtros'}</span>
                    </div>
                </button>

                {showFilters && (
                    <div className="px-5 pb-5 pt-2 border-t mt-1">
                        {/* Quick Filters */}
                        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                            <button
                                onClick={() => {
                                    const today = new Date().toISOString().split('T')[0];
                                    const newFilters = { ...filters, startDate: today, endDate: today };
                                    setFilters(newFilters);
                                    handleApplyFilters(newFilters);
                                }}
                                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-full transition-colors whitespace-nowrap"
                            >
                                Hoy
                            </button>
                            <button
                                onClick={() => {
                                    const now = new Date();
                                    const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
                                    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
                                    const newFilters = { ...filters, startDate: start, endDate: end };
                                    setFilters(newFilters);
                                    handleApplyFilters(newFilters);
                                }}
                                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-full transition-colors whitespace-nowrap"
                            >
                                Este Mes
                            </button>
                            <button
                                onClick={() => {
                                    const now = new Date();
                                    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
                                    const end = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
                                    const newFilters = { ...filters, startDate: start, endDate: end };
                                    setFilters(newFilters);
                                    handleApplyFilters(newFilters);
                                }}
                                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-full transition-colors whitespace-nowrap"
                            >
                                Mes Anterior
                            </button>
                            <button
                                onClick={() => {
                                    const now = new Date();
                                    const start = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
                                    const end = new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0];
                                    const newFilters = { ...filters, startDate: start, endDate: end };
                                    setFilters(newFilters);
                                    handleApplyFilters(newFilters);
                                }}
                                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-full transition-colors whitespace-nowrap"
                            >
                                Este Año
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
                                <input
                                    type="date"
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={filters.startDate}
                                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
                                <input
                                    type="date"
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={filters.endDate}
                                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                                <input
                                    type="text"
                                    placeholder="Nombre o identificación..."
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={filters.customer}
                                    onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Estado Vínculo</label>
                                <select
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={filters.status}
                                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                >
                                    <option value="all">Todos</option>
                                    <option value="linked">Vinculados</option>
                                    <option value="unlinked">Sin vincular</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Monto Mínimo</label>
                                <input
                                    type="number"
                                    placeholder="0"
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={filters.minAmount}
                                    onChange={(e) => setFilters({ ...filters, minAmount: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Monto Máximo</label>
                                <input
                                    type="number"
                                    placeholder="999999999"
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={filters.maxAmount}
                                    onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="mt-4 pt-4 border-t flex gap-2 justify-end">
                            <button
                                onClick={handleClearFilters}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium border"
                            >
                                Limpiar Filtros
                            </button>
                            <button
                                onClick={handleApplyFilters}
                                className="px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors font-bold shadow-md shadow-blue-100 flex items-center gap-2"
                            >
                                <CheckCircle size={18} /> APLICAR FILTROS
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* KPI Cards - Admin Only */}
            {isAdmin() && analytics && !analyticsLoading && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 flex-shrink-0">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl shadow-sm border border-blue-200">
                        <div className="text-sm font-medium text-blue-700 mb-1">Total Vendido</div>
                        <div className="text-2xl font-bold text-blue-900">{formatCurrency(analytics.kpis.total_revenue)}</div>
                        <div className="text-xs text-blue-600 mt-1">{analytics.kpis.total_orders || 0} pedidos</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl shadow-sm border border-green-200">
                        <div className="text-sm font-medium text-green-700 mb-1">Utilidad Total</div>
                        <div className="text-2xl font-bold text-green-900">{formatCurrency(analytics.kpis.total_profit)}</div>
                        <div className="text-xs text-green-600 mt-1">Ganancia neta</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl shadow-sm border border-purple-200">
                        <div className="text-sm font-medium text-purple-700 mb-1">Margen Promedio</div>
                        <div className="text-2xl font-bold text-purple-900">{parseFloat(analytics.kpis.avg_margin || 0).toFixed(1)}%</div>
                        <div className="text-xs text-purple-600 mt-1">Rentabilidad</div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl shadow-sm border border-orange-200">
                        <div className="text-sm font-medium text-orange-700 mb-1">Ticket Promedio</div>
                        <div className="text-2xl font-bold text-orange-900">
                            {formatCurrency(analytics.kpis.total_orders > 0 ? analytics.kpis.total_revenue / analytics.kpis.total_orders : 0)}
                        </div>
                        <div className="text-xs text-orange-600 mt-1">Por pedido</div>
                    </div>
                </div>
            )}

            {/* Sales & Product Trend Charts - Admin Only */}
            {isAdmin() && analytics && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Chart 1: Sales & Orders Trend */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Tendencia de Ventas y Pedidos</h3>
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={analytics.timelineStats || []} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis
                                        dataKey="time_label"
                                        tick={{ fontSize: 12 }}
                                        interval="preserveStartEnd"
                                        angle={-45}
                                        textAnchor="end"
                                        height={60}
                                    />
                                    <YAxis
                                        yAxisId="left"
                                        tickFormatter={(value) => `$${value / 1000}k`}
                                        label={{ value: 'Dinero ($)', angle: -90, position: 'insideLeft' }}
                                    />
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        label={{ value: 'Pedidos (#)', angle: 90, position: 'insideRight' }}
                                    />
                                    <Tooltip
                                        content={({ active, payload, label }) => {
                                            if (active && payload && payload.length) {
                                                // Extract values from payload. Note: payload order depends on chart definition order
                                                const data = payload[0].payload; // Access the raw data object
                                                return (
                                                    <div className="bg-white p-3 border border-gray-200 shadow-xl rounded-lg z-50">
                                                        <p className="font-bold text-gray-700 mb-2 border-b pb-1">{label}</p>

                                                        {/* Total (Explicitly added) */}
                                                        <p className="text-sm flex justify-between gap-4">
                                                            <span className="font-semibold text-gray-800">Total Venta:</span>
                                                            <span className="font-bold">{formatCurrency(data.total_revenue)}</span>
                                                        </p>

                                                        {/* Stacked Components */}
                                                        <p className="text-sm flex justify-between gap-4 text-blue-600">
                                                            <span>Costo:</span>
                                                            <span>{formatCurrency(data.total_cost)}</span>
                                                        </p>
                                                        <p className="text-sm flex justify-between gap-4 text-green-600">
                                                            <span>Utilidad:</span>
                                                            <span>{formatCurrency(data.total_profit)}</span>
                                                        </p>

                                                        {/* Orders (Bottom) */}
                                                        <p className="text-sm flex justify-between gap-4 text-orange-500 mt-2 pt-2 border-t">
                                                            <span>Pedidos:</span>
                                                            <span className="font-bold">{data.order_count}</span>
                                                        </p>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Legend />
                                    {/* Stacked Bars: Cost (Bottom) + Profit (Top) */}
                                    <Bar yAxisId="left" dataKey="total_cost" stackId="revenue" name="Costo" fill="#3b82f6" radius={[0, 0, 0, 0]} />
                                    <Bar yAxisId="left" dataKey="total_profit" stackId="revenue" name="Utilidad" fill="#22c55e" radius={[4, 4, 0, 0]} />
                                    <Line yAxisId="right" type="monotone" dataKey="order_count" name="Pedidos" stroke="#f97316" strokeWidth={3} dot={{ r: 4 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Chart 2: Product Stacked Sales */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Ventas por Producto (Desagregado)</h3>
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={productStackedData.data} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis
                                        dataKey="time_label"
                                        tick={{ fontSize: 12 }}
                                        angle={-45}
                                        textAnchor="end"
                                        height={60}
                                    />
                                    <YAxis
                                        yAxisId="left"
                                        tickFormatter={(value) => `$${value / 1000}k`}
                                    />
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        label={{ value: 'Pedidos (#)', angle: 90, position: 'insideRight' }}
                                    />
                                    <Tooltip
                                        content={({ active, payload, label }) => {
                                            if (active && payload && payload.length) {
                                                const data = payload[0].payload;
                                                return (
                                                    <div className="bg-white p-3 border border-gray-200 shadow-xl rounded-lg z-50">
                                                        <p className="font-bold text-gray-700 mb-2 border-b pb-1">{label}</p>

                                                        <p className="text-sm flex justify-between gap-4">
                                                            <span className="font-semibold text-gray-800">Total Venta:</span>
                                                            <span className="font-bold">{formatCurrency(data.total_revenue)}</span>
                                                        </p>

                                                        <div className="my-2 border-t border-dashed pt-1">
                                                            {productStackedData.products
                                                                .filter(p => data[p] > 0)
                                                                .sort((a, b) => data[b] - data[a])
                                                                .slice(0, 15) // Increased limit, removed scroll
                                                                .map(p => (
                                                                    <p key={p} className="text-xs flex justify-between gap-4 text-gray-600 items-center">
                                                                        <span className="flex items-center gap-2 truncate w-40">
                                                                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: stringToColor(p) }}></span>
                                                                            {p}
                                                                        </span>
                                                                        <span>{formatCurrency(data[p])}</span>
                                                                    </p>
                                                                ))}
                                                        </div>

                                                        <p className="text-sm flex justify-between gap-4 text-orange-500 mt-2 pt-2 border-t">
                                                            <span>Pedidos:</span>
                                                            <span className="font-bold">{data.order_count}</span>
                                                        </p>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: '10px', zIndex: 60, position: 'relative' }} />
                                    {productStackedData.products.slice(0, 20).map((product, index) => (
                                        <Bar
                                            yAxisId="left"
                                            key={product}
                                            dataKey={product}
                                            stackId="a"
                                            fill={stringToColor(product)}
                                            name={product}
                                        />
                                    ))}
                                    <Line yAxisId="right" type="monotone" dataKey="order_count" name="Pedidos" stroke="#f97316" strokeWidth={3} dot={{ r: 4 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}

            {/* Product Participation Charts - Admin Only */}
            {isAdmin() && analytics && analytics.topProducts && analytics.topProducts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 flex-shrink-0">
                    {/* Sales Participation */}
                    <div className="bg-white rounded-xl shadow-sm border p-4">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Participación en Ventas</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={analytics.topProducts}
                                    dataKey="total_revenue"
                                    nameKey="product_name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={100}
                                    minAngle={15}
                                    label={({ name, percent }) => percent > 0 ? `${name}: ${(percent * 100).toFixed(1)}%` : ''}
                                >
                                    {analytics.topProducts.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Profit Participation */}
                    <div className="bg-white rounded-xl shadow-sm border p-4">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Participación en Utilidad</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={analytics.topProducts}
                                    dataKey="total_profit"
                                    nameKey="product_name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={100}
                                    minAngle={15}
                                    label={({ name, percent }) => percent > 0 ? `${name}: ${(percent * 100).toFixed(1)}%` : ''}
                                >
                                    {analytics.topProducts.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}



            <div className="bg-white rounded-xl shadow-sm border h-full flex flex-col p-4">
                {/* Filters */}
                <div className="flex justify-between items-center mb-4">
                    <form onSubmit={handleHistorySearchSubmit} className="relative w-96">
                        <input
                            type="text"
                            placeholder="Buscar por # Orden o Cliente..."
                            className="w-full pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            value={historySearch}
                            onChange={(e) => setHistorySearch(e.target.value)}
                        />
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <button type="submit" className="hidden"></button>
                    </form>
                </div>

                {/* Table */}
                <div className="flex-1 overflow-auto rounded-lg border">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-800 sticky top-0 z-10 text-white">
                            <tr>
                                <th className="p-4 font-bold uppercase tracking-wider text-xs border-b border-slate-700"># Orden</th>
                                <th className="p-4 font-bold uppercase tracking-wider text-xs border-b border-slate-700">Fecha</th>
                                <th className="p-4 font-bold uppercase tracking-wider text-xs border-b border-slate-700">Cliente</th>
                                <th className="p-4 font-bold uppercase tracking-wider text-xs border-b border-slate-700 text-center">Método Pago</th>
                                <th className="p-4 font-bold uppercase tracking-wider text-xs border-b border-slate-700 text-center">Pagado</th>
                                <th className="p-4 font-bold uppercase tracking-wider text-xs border-b border-slate-700 text-center">Entrega</th>
                                <th className="p-4 font-bold uppercase tracking-wider text-xs border-b border-slate-700 text-right">Monto</th>
                                <th className="p-4 font-bold uppercase tracking-wider text-xs border-b border-slate-700 text-right">Utilidad</th>
                                <th className="p-4 font-bold uppercase tracking-wider text-xs border-b border-slate-700 text-right">Util. Acum.</th>
                                <th className="p-4 font-bold uppercase tracking-wider text-xs border-b border-slate-700 text-center">Margen %</th>
                                <th className="p-4 font-bold uppercase tracking-wider text-xs border-b border-slate-700">Estado / Vínculo</th>
                                <th className="p-4 font-bold uppercase tracking-wider text-xs border-b border-slate-700 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {historyLoading ? (
                                <tr><td colSpan="12" className="p-8 text-center text-gray-400">Cargando...</td></tr>
                            ) : historyOrders.length === 0 ? (
                                <tr><td colSpan="12" className="p-8 text-center text-gray-400">No hay pedidos registrados</td></tr>
                            ) : (
                                (() => {
                                    let accumulatedProfit = 0;
                                    return historyOrders.map((order, index) => {
                                        // Determine Link Status
                                        const isLinked = !!order.parent_order_id;
                                        const isBrokenLink = isLinked && !order.parent_order_number;

                                        // Calculate accumulated profit
                                        accumulatedProfit += parseFloat(order.order_profit || 0);

                                        // Determine customer to display (Siigo customer if linked, otherwise original)
                                        const displayCustomerName = isLinked && order.siigo_customer_name
                                            ? order.siigo_customer_name
                                            : order.customer_name;
                                        const displayCustomerId = isLinked && order.siigo_customer_identification
                                            ? order.siigo_customer_identification
                                            : order.customer_identification;

                                        return (
                                            <tr key={order.id} className={`hover:bg-gray-50 ${['cancelado', 'gestion_especial'].includes(order.status) ? 'line-through opacity-50' : ''}`}>
                                                <td className="p-3 font-mono font-medium text-blue-600">{order.order_number}</td>
                                                <td className="p-3 text-sm text-gray-600">
                                                    {new Date(order.created_at).toLocaleDateString('es-CO')} {new Date(order.created_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                                                </td>
                                                <td className="p-3 text-gray-800">
                                                    <div className="font-medium">{displayCustomerName}</div>
                                                    <div className="text-xs text-gray-400">
                                                        {displayCustomerId}
                                                        {isLinked && order.siigo_customer_name && (
                                                            <span className="ml-1 text-green-600" title="Cliente desde Siigo">✓</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-center">
                                                    {(() => {
                                                        const pm = (order.payment_method || '').toLowerCase();
                                                        const labels = {
                                                            'efectivo': { text: 'Efectivo', bg: 'bg-green-100', color: 'text-green-700', icon: '💵' },
                                                            'transferencia': { text: 'Transferencia', bg: 'bg-blue-100', color: 'text-blue-700', icon: '🏦' },
                                                            'nequi': { text: 'Nequi', bg: 'bg-purple-100', color: 'text-purple-700', icon: '📱' },
                                                            'daviplata': { text: 'Daviplata', bg: 'bg-red-100', color: 'text-red-700', icon: '📱' },
                                                            'tarjeta': { text: 'Tarjeta', bg: 'bg-indigo-100', color: 'text-indigo-700', icon: '💳' },
                                                            'mixto': { text: 'Mixto', bg: 'bg-yellow-100', color: 'text-yellow-700', icon: '🔄' },
                                                            'credito': { text: 'Crédito', bg: 'bg-orange-100', color: 'text-orange-700', icon: '📋' },
                                                        };
                                                        const info = labels[pm] || { text: order.payment_method || 'N/A', bg: 'bg-gray-100', color: 'text-gray-600', icon: '💰' };
                                                        return (
                                                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${info.bg} ${info.color}`}>
                                                                {info.icon} {info.text}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="p-3 text-center">
                                                    {(() => {
                                                        const selfPaid = parseFloat(order.paid_amount || 0) >= parseFloat(order.total_amount || 0)
                                                            || (order.validation_status || '').toLowerCase() === 'approved';
                                                        const parentPaid = order.parent_order_id && (
                                                            (order.parent_validation_status || '').toLowerCase() === 'approved'
                                                            || parseFloat(order.parent_paid_amount || 0) >= parseFloat(order.parent_total_amount || 0)
                                                        );
                                                        const isPaid = selfPaid || parentPaid;
                                                        return isPaid ? (
                                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200">
                                                                ✅ Pagado
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-red-50 text-red-600 border border-red-200">
                                                                ⏳ Pendiente
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="p-3 text-center">
                                                    {(() => {
                                                        const dm = (order.delivery_method || '').toLowerCase();
                                                        const labels = {
                                                            'recoge_bodega': { text: 'Bodega', bg: 'bg-amber-100', color: 'text-amber-700', icon: '🏭' },
                                                            'mensajero': { text: 'Mensajero', bg: 'bg-cyan-100', color: 'text-cyan-700', icon: '🛵' },
                                                            'transportadora': { text: 'Transportadora', bg: 'bg-indigo-100', color: 'text-indigo-700', icon: '🚚' },
                                                            'recoge_posterior': { text: 'Recoge Después', bg: 'bg-orange-100', color: 'text-orange-700', icon: '⏰' },
                                                            'local': { text: 'Local', bg: 'bg-teal-100', color: 'text-teal-700', icon: '🏪' },
                                                        };
                                                        const info = labels[dm] || { text: order.delivery_method || 'N/A', bg: 'bg-gray-100', color: 'text-gray-500', icon: '📦' };
                                                        return (
                                                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${info.bg} ${info.color}`}>
                                                                {info.icon} {info.text}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="p-3 font-bold text-gray-700 text-right">
                                                    {formatCurrency(order.total_amount)}
                                                </td>
                                                <td className="p-3 text-right">
                                                    <span className={`font-semibold ${parseFloat(order.order_profit) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                                        {formatCurrency(order.order_profit)}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-right">
                                                    <span className="font-semibold text-blue-700">
                                                        {formatCurrency(accumulatedProfit)}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-center">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${parseFloat(order.avg_profit_margin) >= 30 ? 'bg-green-100 text-green-700' :
                                                        parseFloat(order.avg_profit_margin) >= 20 ? 'bg-yellow-100 text-yellow-700' :
                                                            parseFloat(order.avg_profit_margin) >= 0 ? 'bg-orange-100 text-orange-700' :
                                                                'bg-red-100 text-red-700'
                                                        }`}>
                                                        {parseFloat(order.avg_profit_margin || 0).toFixed(1)}%
                                                    </span>
                                                </td>
                                                <td className="p-3">
                                                    {isLinked ? (
                                                        isBrokenLink ? (
                                                            <div className="inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs shadow-sm border border-red-200" title={`ID Pedido Padre: ${order.parent_order_id} (No encontrado en DB)`}>
                                                                <AlertCircle size={12} className="mr-1" />
                                                                Error Vínculo (ID: {order.parent_order_id})
                                                            </div>
                                                        ) : (
                                                            <div className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs shadow-sm border border-green-200">
                                                                <CheckCircle size={12} className="mr-1" />
                                                                Vinculado: {order.parent_order_number}
                                                            </div>
                                                        )
                                                    ) : (
                                                        <div className="inline-flex items-center px-2 py-1 rounded-full bg-orange-100 text-orange-700 text-xs shadow-sm border border-orange-200">
                                                            <AlertCircle size={12} className="mr-1" />
                                                            Sin vincular
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-3 text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <button
                                                            onClick={() => handlePrintHistoryOrder(order)}
                                                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded border hover:border-gray-300 transition-colors"
                                                            title="Imprimir Voucher"
                                                        >
                                                            <Printer size={16} />
                                                        </button>

                                                        {/* Show Link/Edit button always */}
                                                        <button
                                                            onClick={() => openLinkModal(order)}
                                                            className={`p-1.5 rounded border transition-colors ${isBrokenLink ? 'text-red-600 border-red-200 hover:bg-red-50' :
                                                                isLinked ? 'text-blue-600 border-blue-200 hover:bg-blue-50' :
                                                                    'text-purple-600 border-purple-100 hover:border-purple-300 hover:bg-purple-50'
                                                                }`}
                                                            title={isLinked ? "Editar Vínculo" : "Vincular a Factura Siigo"}
                                                        >
                                                            {isLinked ? <Edit size={16} /> : <LinkIcon size={16} />}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    });
                                })()
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL LINK */}
            {linkModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-xl p-6 w-[500px] max-w-full m-4 border-t-4 border-purple-500">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h2 className="text-xl font-bold flex items-center gap-2">
                                    <LinkIcon className="text-purple-600" />
                                    {orderToLink?.parent_order_id ? 'Editar Vínculo' : 'Vincular Pedido'}
                                    <span className="text-gray-500 font-mono text-base">{orderToLink?.order_number}</span>
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Busca la factura o pedido de Siigo (FV-2-...)
                                </p>
                            </div>

                            {/* UNLINK BUTTON IF LINKED */}
                            {orderToLink?.parent_order_id && (
                                <button
                                    onClick={handleUnlink}
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded text-sm font-medium flex items-center gap-1 transition-colors border border-transparent hover:border-red-100"
                                >
                                    <Unlink size={14} />
                                    Desvincular
                                </button>
                            )}
                        </div>

                        <div className="relative mb-6">
                            <input
                                type="text"
                                placeholder="Buscar FV-2-..."
                                className="w-full pl-9 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none text-lg"
                                value={linkInvoiceSearch}
                                onChange={(e) => handleSearchLinkInvoice(e.target.value)}
                                autoFocus
                            />
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />

                            {/* Dropdown results */}
                            {linkInvoices.length > 0 && (
                                <div className="absolute top-full left-0 w-full bg-white border shadow-xl rounded-b-lg z-10 mt-1 max-h-60 overflow-y-auto">
                                    {linkInvoices.map(inv => (
                                        <button
                                            key={inv.id}
                                            onClick={() => submitLink(inv)}
                                            className="w-full text-left p-3 hover:bg-purple-50 border-b last:border-0 transition-colors group"
                                        >
                                            <div className="font-bold text-gray-800 group-hover:text-purple-700">{inv.order_number}</div>
                                            <div className="text-xs text-gray-500 flex justify-between">
                                                <span>{inv.customer_name}</span>
                                                <span className="font-medium text-purple-600">${new Intl.NumberFormat().format(inv.total_amount)}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {linkInvoices.length === 0 && linkInvoiceSearch.length >= 3 && (
                                <div className="absolute top-full left-0 w-full bg-white border shadow py-3 text-center text-sm text-gray-500 mt-1">
                                    No se encontraron resultados para "{linkInvoiceSearch}"
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setLinkModalOpen(false)}
                                className="px-5 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InternalOrdersHistoryPage;
